/**
 * Typed client for /admin-services/networks/:tab — backs the Networks screen.
 *
 * Three network tabs:
 *   - mysterium  (VPN exit-node bandwidth relay)
 *   - iproyal    (Residential proxy bandwidth)
 *   - sentinel   (Distributed storage)
 *
 * The endpoint returns { success, data } via utils.handleSuccess. Use
 * unwrap() from @/api/client to peel the Axios envelope (.data) and the
 * API envelope (.data.data) in one call.
 *
 * When a network is not configured the backend returns 404 with
 * { errors: { msg: 'NOT_CONFIGURED' } }. The hook treats this as
 * configured=false rather than an error so the "Not connected" state
 * renders cleanly.
 */
import { client, unwrap } from './client';

export type NetworkTab = 'mysterium' | 'iproyal' | 'sentinel';

export interface TopNode {
  id: string;
  country?: string;
  lat?: number;
  lng?: number;
  earnings: number;
  uptime?: number; // percent 0-100
}

export interface CountryActivity {
  country: string;
  lat?: number;
  lng?: number;
  count?: number;
}

export interface NetworkData {
  configured: boolean;
  status: string; // 'up' | 'down' | etc.
  activeNodes: number;
  // Bandwidth-specific (mysterium, iproyal)
  throughputBps?: number;
  bytesRouted24h?: number;
  // Storage-specific (sentinel)
  allocatedBytes?: number;
  auditsPassed24h?: number;
  auditsFailed24h?: number;
  // Common earnings
  earnings24hUsd: number;
  dailyEarnings?: number[]; // sparkline series
  topNodes: TopNode[];
  countries: CountryActivity[];
}

export async function fetchNetworkTab(tab: NetworkTab): Promise<NetworkData> {
  return unwrap<NetworkData>(
    client.get<{ data: NetworkData }>(`/admin-services/networks/${tab}`)
  );
}
